import { Link } from "@tanstack/react-router";

/**
 * Router-level error boundary UI (wired as defaultErrorComponent). Catches any
 * render/resolution failure within the shell — e.g. a ContextChain resolution
 * error — and shows a defined error state instead of a white screen (Phase-0
 * acceptance: "error → error boundary, not white screen").
 */
export function ShellError({ error, reset }: { error: Error; reset?: () => void }) {
  return (
    <div
      role="alert"
      className="flex flex-col items-start gap-3 p-8 max-w-xl text-[var(--color-text-primary)]"
    >
      <h1 className="text-xl font-semibold">Something went wrong</h1>
      <p className="text-sm text-[var(--color-text-secondary)]">
        The workspace couldn’t render this view. The error has been contained — your
        session is intact.
      </p>
      <pre className="text-xs whitespace-pre-wrap bg-[var(--color-surface-muted)] text-[var(--color-text-secondary)] rounded-md p-3 w-full overflow-auto">
        {error?.message ?? "Unknown error"}
      </pre>
      <div className="flex items-center gap-2">
        {reset && (
          <button
            type="button"
            onClick={reset}
            className="h-9 px-3 rounded-md text-sm bg-[var(--color-accent)] text-[var(--color-text-inverse)] hover:bg-[var(--color-accent-hover)]"
          >
            Try again
          </button>
        )}
        <Link
          to="/"
          className="h-9 px-3 inline-flex items-center rounded-md text-sm border border-[var(--color-border-default)] text-[var(--color-text-primary)] hover:bg-[var(--color-surface-muted)]"
        >
          Back to home
        </Link>
      </div>
    </div>
  );
}
